var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect } from 'react';
import { createMemoryRouter, createRoutesFromElements, Route, useLocation, useParams, } from 'react-router';
import Intro from '../screens/Intro';
import Info from '../screens/Info';
import AmountSelection from '../screens/AmountSelection';
import AmountSelectionDDForms from '../screens/DirectDepositForms/AmountSelection';
import ConfirmationScreen from '../screens/DirectDepositForms/ConfirmationScreen';
import EmployerNameEntry from '../screens/DirectDepositForms/EmployerNameEntry';
import UserNameEntry from '../screens/DirectDepositForms/UserNameEntry';
import MultipleAccountSelection from '../screens/DirectDepositForms/MultipleAccountSelection';
import SignatureStyleSelection from '../screens/DirectDepositForms/SignatureStyleSelection';
import DocumentDisplay from '../screens/DirectDepositForms/DocumentDisplay';
import Success from '../screens/Success';
import AppMux from '../screens/AppMux';
import SearchMain from '../screens/Search/SearchMain';
import SearchPlatforms from '../screens/Search/SearchPlatforms';
import SearchWorkday from '../screens/Search/SearchWorkday';
import Terms from '../screens/Terms';
import Privacy from '../screens/Privacy';
import Error from '../screens/Error';
import Login from '../screens/LinkApi/Login';
import DDS from '../screens/LinkApi/DDS';
import BillSwitch from '../screens/LinkApi/BillSwitch';
import { useAppDispatch } from '../state';
import routeSurfaceMappings from '../segment/event-surfaces-mapping';
import { generateSearchSessionId, updateEventSource, } from '../state/slices/slice-segment';
import eventQueue from '../events/queue';
import SwitchConfirmation from '../screens/SwitchConfirmation';
import CardSwitch from '../screens/LinkApi/CardSwitch';
import CardSwitchConfirm from '../screens/CardSwitch/CardSwitchConfirm';
import DDSCardSuccess from '../screens/CardSwitch/DDSCardSuccess';
import DDSError from '../screens/CardSwitch/DDSError';
import CardError from '../screens/CardSwitch/CardError';
import ErrorBoundary from '../ErrorBoundary';
import DDFormPrimer from '../screens/DirectDepositForms/DDFormPrimer';
import DocumentUploadsList from '../screens/DocumentUploads/DocumentUploadsList';
import DocumentUploadsPrimer from '../screens/DocumentUploads/DocumentUploadsPrimer';
import DocumentUploadsSubmitted from '../screens/DocumentUploads/DocumentUploadsSubmitted';
import DocumentUploadsDelete from '../screens/DocumentUploads/DocumentUploadsDelete';
import AdpIframe from '../screens/AdpIframe/AdpIframe';
import DocumentUploadsRepair from '../screens/DocumentUploads/DocumentUploadsRepair';
import BankInfoInput from '../screens/BankInfoInput';
import GuidedSwitchPrimerScreen from '../screens/LinkApi/GuidedSwitchPrimerScreen';
import { ScreenLoadingSkeleton } from '../views/ScreenLoadingSkeleton';
import { useLoadingSkeleton } from '../hooks/useLoadingSkeleton';
import ModalContentWrapper from '../ModalContentWrapper';
import ManualInstructionsFallback from '../screens/ManualInstructionsFallback';
import ManualInstructionsDisplay from '../screens/ManualInstructionsDisplay';
import Lookup from '../screens/MultiSupplierEligibility/Lookup';
import LookupResults from '../screens/MultiSupplierEligibility/LookupResults';
import LookupHelp from '../screens/MultiSupplierEligibility/LookupResultsHelp';
import ReturnToSearch from '../views/MultiSupplierEligibility/ReturnToSearch';
import AccountSelection from '../screens/AccountSelection';
import DDFormBankInfoInput from '../screens/DirectDepositForms/DDFormBankInfoInput';
import WizardIncomeSources from '../screens/Wizard/WizardIncomeSources';
import WizardPaymentMethods from '../screens/Wizard/WizardPaymentMethods';
import WizardIncomeSourceW2 from '../screens/Wizard/WizardIncomeSourceW2';
import WizardIncomeSource1099 from '../screens/Wizard/WizardIncomeSource1099';
import WizardIncomeSourceBenefits from '../screens/Wizard/WizardIncomeSourceBenefits';
import WizardError from '../screens/Wizard/WizardError';
import HomerIframe from '../screens/HomerIframe';
import PIIDOBEntry from '../screens/PIIDataEntry/DOBEntry';
import PIIDataSSN4 from '../screens/PIIDataEntry/SSN4Entry';
import PIIDataReview from '../screens/PIIDataEntry/Review';
import PIITelEntry from '../screens/PIIDataEntry/TelEntry';
import BillCancellation from '../screens/LinkApi/BillCancellation';
var p = function (routeString) { return routeString; };
var useEventSurface = function () {
    var location = useLocation();
    var path = location.pathname;
    var params = useParams();
    // This will work for all static paths, but not dynamic paths, as the resolved
    // and unresolved paths will differ (e.g. "/delete/:fileId" vs "delete/123").
    if (path in routeSurfaceMappings) {
        return routeSurfaceMappings[path];
    }
    // For dynamic paths, we will need to manually re-create the unresolved path
    // first (e.g. "/delete/123" -> "/delete/:fileId"), as react-router does not
    // have a good built-in way to do this.
    var paramValueToNameMap = Object.fromEntries(Object.entries(params).map(function (_a) {
        var k = _a[0], v = _a[1];
        return [v, k];
    }));
    var unresolvedDynamicPath = Object.keys(paramValueToNameMap).reduce(function (pathStr, paramValue) {
        var paramName = paramValueToNameMap[paramValue];
        return pathStr.replace(paramValue, ":".concat(paramName));
    }, path);
    if (unresolvedDynamicPath in routeSurfaceMappings) {
        return routeSurfaceMappings[unresolvedDynamicPath];
    }
    return null;
};
var Screen = function (_a) {
    var children = _a.children, loadingSkeletonEnabled = _a.loadingSkeletonEnabled;
    var location = useLocation();
    var dispatch = useAppDispatch();
    var eventSource = useEventSurface();
    var showLoadingSkeleton = useLoadingSkeleton(loadingSkeletonEnabled).showLoadingSkeleton;
    useEffect(function () {
        var path = location.pathname;
        if (path.startsWith('/search')) {
            dispatch(generateSearchSessionId());
        }
        if (eventSource) {
            dispatch(updateEventSource(eventSource));
            eventQueue.emit('impression', {});
        }
    }, [location.pathname, dispatch, eventSource]);
    if (showLoadingSkeleton) {
        return React.createElement(ScreenLoadingSkeleton, null);
    }
    // Note: we purposely want to render <ModalContentWrapper> here (instead of
    // somewhere higher up in the component tree) so that the exit confirmation is
    // contained within the router provider, and thus can use `useNavigate`.
    return React.createElement(ModalContentWrapper, null, children);
};
var getProps = function (_a) {
    var routeString = _a.routeString, Element = _a.element, _b = _a.loadingSkeletonEnabled, loadingSkeletonEnabled = _b === void 0 ? true : _b;
    return ({
        path: routeString,
        element: (React.createElement(ErrorBoundary, null,
            React.createElement(Screen, { loadingSkeletonEnabled: loadingSkeletonEnabled, 
                // Pass a key so that the component is reinitialized between routes, so
                // that the loading skeleton state is properly reset on every new screen.
                // TODO: `routeString` is not guaranteed to be unique, use the full path
                key: routeString }, Element && React.createElement(Element, null)))),
        errorElement: React.createElement(Error, null),
    });
};
var createRouter = function (opts) {
    return createMemoryRouter(createRoutesFromElements(React.createElement(React.Fragment, null,
        React.createElement(Route, __assign({}, getProps({
            routeString: '/',
            element: AppMux,
            // Don't show loading skeleton as this is not a "real" screen,
            // and already renders the loading skeleton by default.
            loadingSkeletonEnabled: false,
        }))),
        React.createElement(Route, __assign({}, getProps({ routeString: 'adp_iframe', element: AdpIframe }))),
        React.createElement(Route, __assign({}, getProps({ routeString: 'intro', element: Intro }))),
        React.createElement(Route, __assign({}, getProps({ routeString: 'info', element: Info }))),
        React.createElement(Route, __assign({}, getProps({ routeString: 'terms', element: Terms }))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'bill-navigator-iframe',
            element: HomerIframe,
        }))),
        React.createElement(Route, __assign({}, getProps({ routeString: 'privacy-policy', element: Privacy }))),
        React.createElement(Route, { path: p('search') },
            React.createElement(Route, __assign({}, getProps({ routeString: 'search', element: SearchMain }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'platforms',
                element: SearchPlatforms,
            }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'workday', element: SearchWorkday })))),
        React.createElement(Route, { path: p('link-api') },
            React.createElement(Route, __assign({}, getProps({
                routeString: 'login',
                element: Login,
                // Will show the loading spinner on mount, so avoid showing two
                // consecutive loaders.
                loadingSkeletonEnabled: false,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'dds',
                element: DDS,
                loadingSkeletonEnabled: false,
            }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'card-switch', element: CardSwitch }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'bill-switch', element: BillSwitch }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'bill-cancellation',
                element: BillCancellation,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'guided-switch-primer',
                element: GuidedSwitchPrimerScreen,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'manual-instructions-fallback',
                element: ManualInstructionsFallback,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'manual-instructions-display',
                element: ManualInstructionsDisplay,
            })))),
        React.createElement(Route, __assign({}, getProps({ routeString: 'error', element: Error }))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'amount-selection',
            element: AmountSelection,
        }))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'multiple-amount-selection',
            element: MultipleAccountSelection,
        }))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'bank-info-input',
            element: BankInfoInput,
        }))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'account-selection',
            element: AccountSelection,
        }))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'switch-confirmation',
            element: SwitchConfirmation,
        }))),
        React.createElement(Route, { path: p('dd-form') },
            React.createElement(Route, __assign({}, getProps({
                routeString: 'confirmation-screen',
                element: ConfirmationScreen,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'employer-name-entry',
                element: EmployerNameEntry,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'user-name-entry',
                element: UserNameEntry,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'signature-style-selection',
                element: SignatureStyleSelection,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'document-display',
                element: DocumentDisplay,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'amount-selection',
                element: AmountSelectionDDForms,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'bank-info-input',
                element: DDFormBankInfoInput,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'multiple-account-selection',
                element: MultipleAccountSelection,
            }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'primer', element: DDFormPrimer })))),
        React.createElement(Route, { path: p('pii-data') },
            React.createElement(Route, __assign({}, getProps({ routeString: 'tel', element: PIITelEntry }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'dob', element: PIIDOBEntry }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'ssn4', element: PIIDataSSN4 }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'review', element: PIIDataReview })))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'success',
            element: Success,
            loadingSkeletonEnabled: false,
        }))),
        React.createElement(Route, { path: p('card-switch') },
            React.createElement(Route, __assign({}, getProps({
                routeString: 'confirm',
                element: CardSwitchConfirm,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'dds-card-success',
                element: DDSCardSuccess,
            }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'dds-error', element: DDSError }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'card-error', element: CardError })))),
        React.createElement(Route, { path: p('document-uploads') },
            React.createElement(Route, __assign({}, getProps({
                routeString: 'primer',
                element: DocumentUploadsPrimer,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'submitted',
                element: DocumentUploadsSubmitted,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'delete/:fileId',
                element: DocumentUploadsDelete,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'repair/:fileId',
                element: DocumentUploadsRepair,
            }))),
            React.createElement(Route, __assign({}, getProps({ routeString: 'list', element: DocumentUploadsList })))),
        React.createElement(Route, { path: p('multi-supplier-eligibility') },
            React.createElement(Route, __assign({}, getProps({
                routeString: 'lookup',
                element: Lookup,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'results',
                element: LookupResults,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'help',
                element: LookupHelp,
            })))),
        React.createElement(Route, { path: p('wizard') },
            React.createElement(Route, __assign({}, getProps({
                routeString: 'income-sources',
                element: WizardIncomeSources,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'payment-methods',
                element: WizardPaymentMethods,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'income-source-w2',
                element: WizardIncomeSourceW2,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'income-source-1099',
                element: WizardIncomeSource1099,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'income-source-benefits',
                element: WizardIncomeSourceBenefits,
            }))),
            React.createElement(Route, __assign({}, getProps({
                routeString: 'wizard-error',
                element: WizardError,
            })))),
        React.createElement(Route, __assign({}, getProps({
            routeString: 'return_to_search',
            element: ReturnToSearch,
        }))))), opts);
};
var singletons = {};
export var resetRouter = function () {
    singletons.router = createRouter();
};
export var getRouter = function () {
    if (!singletons.router)
        resetRouter();
    return singletons.router;
};
